/**
 * A TIMELINE BUILT IN THE BROWSER, so proprietary bars never leave it.
 *
 * <h2>The tension this resolves, stated plainly</h2>
 *
 * The brief's rationale for parsing a CSV client-side is that "proprietary client data never needs
 * to touch the backend". The parse already works that way — `CustomDataUpload` reads the file with
 * `FileReader`, keeps the bars in React state and drops the text, persisting nothing.
 *
 * <p>But the ALGO LEDGER is the engine's, and the engine runs on the server. There is no way to
 * have the engine's opinion of a private series without sending the series. The existing
 * `customSeries` path does exactly that — it POSTs the parsed bars to `/api/simulation/timeline` —
 * and for a customer evaluating the strategy that is the point.
 *
 * <p>So it is a CHOICE, made where the data is chosen, rather than a default nobody is told about:
 *
 * <pre>
 *   engine ON    the parsed bars are POSTed. The algo book, the audit and the bands are real.
 *   engine OFF   nothing is sent. This module stands in for the run: the chart unveils, the
 *                operator trades, and the algo column is empty and says why.
 * </pre>
 *
 * <h2>What a local timeline is and is not</h2>
 *
 * It is the FRAMES and nothing else: one per bar, in order, so the clock has something to walk and
 * every bar-indexed decoration resolves. It carries no trades, no transitions, no intents, no stop
 * updates, no bands and no statistics, because all of those are the engine's findings and there has
 * been no engine.
 *
 * <p>That is a real limitation and the UI says so rather than presenting an empty algo column as a
 * strategy that found nothing. `local: true` is on the object so every consumer can tell the
 * difference between "the engine ran and took no trades" and "no engine ran".
 */

/** Marks a timeline as having been built here rather than by a run. */
export const LOCAL_TIMELINE_WARNING =
  'No engine ran: these bars were never sent anywhere. The chart, your own book and the '
  + 'structural overlays you supplied are live; the algo column is empty because there is no '
  + 'algorithm — switch the engine on in the setup modal to have the bars priced by it.';

/**
 * Frames from a bar series.
 *
 * @param symbol the label the transport shows
 * @param barData the chart's own `{dates, ohlc, volumes}` — `[open, close, low, high]` tuples
 * @returns a timeline in the shape `/api/simulation/timeline` returns, or null with no bars
 */
export function buildLocalTimeline(symbol, barData) {
  const dates = Array.isArray(barData?.dates) ? barData.dates : [];
  const ohlc = Array.isArray(barData?.ohlc) ? barData.ohlc : [];
  const volumes = Array.isArray(barData?.volumes) ? barData.volumes : [];
  if (dates.length === 0 || ohlc.length === 0) return null;

  const frames = [];
  for (let i = 0; i < dates.length; i++) {
    const bar = ohlc[i];
    // A padded column has no price. It is skipped rather than framed: the clock would otherwise
    // walk into the synthetic future, and every figure on screen would be about a bar that does
    // not exist.
    if (!bar || bar[1] == null) continue;
    frames.push({
      index: frames.length,
      total: 0,                       // filled in below, once the count is known
      // MIDNIGHT UTC, explicitly. Every consumer string-slices the first ten characters to get a
      // session date — `sessionDateOf` — so the time part is never read. An offset is attached
      // anyway so the shape matches a real frame and nothing downstream has to special-case it.
      time: `${dates[i]}T00:00:00Z`,
      timeframe: 'daily',
      open: bar[0],
      high: bar[3],
      low: bar[2],
      close: bar[1],
      volume: Number.isFinite(volumes[i]) ? volumes[i] : 0,
      // CASH on every bar, because nothing is trading. Not 'UNKNOWN': the state is the engine's
      // vocabulary and CASH is the honest member of it for a book holding nothing.
      state: 'CASH',
      trade: null,
      account: null,
      diagnostics: null,
    });
  }
  if (frames.length === 0) return null;
  frames.forEach((f) => { f.total = frames.length; });

  return {
    symbol: symbol || null,
    window: null,
    frames,
    // EMPTY, not absent. Every reader of these does `Array.isArray(...) ? … : []`, so either
    // works — but an empty array is a statement that the engine found nothing to report, where a
    // missing key invites a reader to wonder whether the field was forgotten.
    transitions: [],
    intents: [],
    stopUpdates: [],
    breakpoints: [],
    trades: [],
    openTrade: null,
    statistics: null,
    netStatistics: null,
    meta: null,
    warnings: [LOCAL_TIMELINE_WARNING],
    timeBands: [],
    /** The flag that distinguishes "no engine ran" from "the engine took no trades". */
    local: true,
  };
}

/** Whether a timeline was built here rather than by a run. */
export const isLocalTimeline = (timeline) => !!(timeline && timeline.local === true);
